import type { AxiosResponse } from "axios";
import { AxiosError } from "axios";

import type {
    AssignCompanyParams,
    CompanyGroupRepository,
    CreateCompanyGroupParams,
    CurrentCompanyGroup,
    ICompanyGroup,
    ICompanyGroupMember,
    SharedFields,
    UpdateCompanyGroupParams,
} from ".";

// Mirrors the backend SHAREABLE_BENEFICIARY_FIELDS catalog.
export const SHAREABLE_BENEFICIARY_FIELDS_CATALOG = [
    "email",
    "name",
    "surname",
    "pseudonym",
    "birthDate",
    "street",
    "zipCode",
    "city",
    "country",
    "phoneNumber",
    "siretNumber",
    "socialSecurityNumber",
    "activityType",
    "iban",
    "routingNumber",
    "swift",
    "ribOwnerName",
    "bankingMetadata",
    "bankingStatus",
    "paypalEmail",
    "bankCheck",
];

export const companyGroupsFixtures: ICompanyGroup[] = [
    { id: 1, name: "Groupe Hachette", createdAt: new Date("2026-01-10").toISOString(), modifiedAt: null },
    { id: 2, name: "Editis", createdAt: new Date("2026-02-15").toISOString(), modifiedAt: null },
];

// Emulates the backend 409 (a company belongs to at most one group).
const conflictError = (message = "ResourceAlreadyExists") => {
    const error: any = new Error(message);
    error.response = { status: 409, data: { message } };
    return error;
};

export class CompanyGroupInMem implements CompanyGroupRepository {
    groups: ICompanyGroup[];
    // companyId -> { groupId, name, avatarUrl }
    companyAssignments: Record<number, ICompanyGroupMember> = {};
    // groupId -> selected shared field keys
    groupSharedFields: Record<number, string[]> = {};
    private nextId: number;
    #currentCompanyGroup: CurrentCompanyGroup | undefined;

    constructor(initialGroups: ICompanyGroup[] = []) {
        this.groups = [...initialGroups];
        this.nextId = this.groups.reduce((max, g) => Math.max(max, g.id), 0) + 1;
    }

    async fetchCompanyGroups(): Promise<ICompanyGroup[]> {
        // Return copies — the store freezes payloads (immer), so never hand out live refs.
        return this.groups.map((g) => ({ ...g })).sort((a, b) => a.name.localeCompare(b.name));
    }

    async createCompanyGroup(params: CreateCompanyGroupParams): Promise<{ id: number; name: string }> {
        const group: ICompanyGroup = {
            id: this.nextId++,
            name: params.name,
            createdAt: new Date().toISOString(),
            modifiedAt: null,
        };
        this.groups.push(group);
        return { id: group.id, name: group.name };
    }

    async updateCompanyGroup(params: UpdateCompanyGroupParams): Promise<ICompanyGroup> {
        const index = this.groups.findIndex((g) => g.id === params.id);
        if (index === -1) throw new Error("Group not found");
        const updated: ICompanyGroup = {
            ...this.groups[index],
            name: params.name,
            modifiedAt: new Date().toISOString(),
        };
        this.groups[index] = updated;
        return { ...updated };
    }

    async deleteCompanyGroup(id: number): Promise<void> {
        this.groups = this.groups.filter((g) => g.id !== id);
        // Detach members (ON DELETE SET NULL)
        Object.entries(this.companyAssignments).forEach(([companyId, member]) => {
            if (member.groupId === id) delete this.companyAssignments[+companyId];
        });
    }

    async fetchGroupCompanies(groupId: number): Promise<ICompanyGroupMember[]> {
        return Object.values(this.companyAssignments).filter((m) => m.groupId === groupId);
    }

    async assignCompany({ groupId, companyId }: AssignCompanyParams): Promise<void> {
        const current = this.companyAssignments[companyId];
        if (current && current.groupId !== groupId) throw conflictError();
        this.companyAssignments[companyId] = { id: companyId, name: null, avatarUrl: null, groupId };
    }

    async unassignCompany({ companyId }: AssignCompanyParams): Promise<void> {
        delete this.companyAssignments[companyId];
    }

    async fetchSharedFields(groupId: number): Promise<SharedFields> {
        return {
            fields: [...(this.groupSharedFields[groupId] ?? [])],
            catalog: [...SHAREABLE_BENEFICIARY_FIELDS_CATALOG],
        };
    }

    async setSharedFields(groupId: number, fields: string[]): Promise<{ fields: string[] }> {
        this.groupSharedFields[groupId] = [...fields];
        return { fields: [...fields] };
    }

    async setCurrentGroup(currentCompanyGroup: CurrentCompanyGroup | undefined) {
        this.#currentCompanyGroup = currentCompanyGroup;
    }
    async getCurrent(): Promise<CurrentCompanyGroup> {
        if (!this.#currentCompanyGroup) {
            throw new AxiosError("No company group", "NO_COMPANY_GROUP", undefined, {}, {
                status: 404,
            } as AxiosResponse);
        }
        return this.#currentCompanyGroup;
    }
}
